import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface FinancialData {
  responses: AssessmentResponse[];
  scores: {
    overall: number;
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const FinancialAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalQuestions = 8;

  // Financial assessment questions
  const questions = [
    { id: '1', question: 'How frequently do you worry about being able to meet normal monthly living expenses?' },
    { id: '2', question: 'How confident are you that you could find the money to pay for a major unexpected expense?' },
    { id: '3', question: 'How often do you find yourself just getting by paycheck to paycheck?' },
    { id: '4', question: 'How satisfied are you with your present financial situation?' },
    { id: '5', question: 'How often do you feel you are in control of your financial situation?' },
    { id: '6', question: 'How frequently do you worry about being able to meet future financial needs?' },
    { id: '7', question: 'How stressed do you feel about your personal finances in general?' },
    { id: '8', question: 'How would you rate your overall financial well-being?' }
  ];

  // Answer options (1-10 scale)
  const answerOptions = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
    { value: 6, label: '6' },
    { value: 7, label: '7' },
    { value: 8, label: '8' },
    { value: 9, label: '9' },
    { value: 10, label: '10' }
  ];

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;

    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      return [...filtered, newResponse];
    });
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    } else {
      navigate('/panel/learn');
    }
  };

  const calculateScores = (responses: AssessmentResponse[]) => {
    // Reversed items: questions 1, 3, 6, 7 (Score = 11 - Value)
    // Direct items: questions 2, 4, 5, 8 (Score = Value)
    const reversedQuestions = ['1', '3', '6', '7'];

    const overallScore = responses.reduce((sum, r) => {
      const isReversed = reversedQuestions.includes(r.questionId);
      const score = isReversed ? 11 - r.answer : r.answer;
      return sum + score;
    }, 0);

    return {
      overall: overallScore
    };
  };

  const interpretResults = (scores: any) => {
    const { overall } = scores;

    let level: 'Low' | 'Moderate' | 'High';
    let interpretation: string;
    let recommendations: string[];

    // Scoring: Sum 8–80.
    // Interpretation: 8–29 High distress; 30–49 Moderate distress; 50–80 Low distress / High financial well-being.

    if (overall >= 50) {
      level = 'Low';
      interpretation = `Low Distress / High Well-being (Score: ${overall}/80). The individual feels secure, in control, and has low levels of financial anxiety.`;

      recommendations = [
        'Continue your excellent financial management practices',
        'Consider helping others learn financial management skills',
        'Explore advanced financial planning tools and techniques',
        'Share your financial success strategies',
        'Focus on optimizing your financial portfolio for maximum efficiency'
      ];
    } else if (overall >= 30) {
      level = 'Moderate';
      interpretation = `Moderate Distress (Score: ${overall}/80). The individual may be meeting basic needs but lacks a safety net or feels consistent anxiety about the future.`;

      recommendations = [
        'Create a detailed monthly budget and stick to it',
        'Build an emergency fund (aim for 3-6 months expenses)',
        'Track all income and expenses to understand spending patterns',
        'Set clear financial priorities and goals',
        'Consider consulting a financial advisor for personalized guidance',
        'Learn about basic financial planning principles',
        'Automate savings and bill payments to reduce stress'
      ];
    } else {
      level = 'High';
      interpretation = `High Distress (Score: ${overall}/80). The individual is likely struggling to meet basic needs and experiences significant financial strain.`;

      recommendations = [
        '⚠️ PRIORITY: Start with basic income and expense tracking',
        'Write down all monthly income sources and expenses',
        'Identify essential vs. non-essential expenses and cut unnecessary spending',
        'Seek help from a financial counselor or credit counseling service',
        'Build a small emergency fund as quickly as possible',
        'Use free budgeting apps to simplify financial management',
        'Start with small, achievable financial goals',
        'Consider speaking with a trusted financial advisor for immediate support'
      ];
    }

    return { level, interpretation, recommendations };
  };

  const handleSubmit = async () => {
    if (responses.length < 8) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsSubmitting(true);
    let response: Response | undefined;

    try {
      const scores = calculateScores(responses);
      const results = interpretResults(scores);

      // Debug logging
      console.log('Financial Assessment Debug:');
      console.log('Responses:', responses.map(r => `${r.questionId}:${r.answer}`).join(', '));
      console.log('Calculated Score:', scores.overall);
      console.log('Result Level:', results.level);
      console.log('Expected: Low for >=50, Moderate for 30-49, High for <30');

      // Only send responses for creation; backend calculates scores/results
      const assessmentData = { responses };

      response = await fetch(API_CONFIG.ENDPOINTS.FINANCIAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        throw new Error(errorData?.error || 'Failed to submit assessment');
      }

      const result = await response.json();

      // Clear Insights page cache to force refresh
      const userId = JSON.parse(localStorage.getItem('user') || '{}').id;
      const insightsCacheKey = userId ? `lifepsensor_insights_cache_${userId}` : 'lifepsensor_insights_cache';
      localStorage.removeItem(insightsCacheKey);

      // Notify InsightsPage about the update
      assessmentUpdateNotifier.notifyUpdate('financial');

      // Navigate to completion page
      navigate('/panel/learn/financial-assessment/completion', {
        state: { assessmentResult: result.data }
      });

    } catch (error) {

      let errorMessage = 'Error submitting assessment. Please try again.';

      // Show more detailed error information
      if (error instanceof Error) {
        if (error.message === 'SESSION_EXPIRED') {
          errorMessage = 'Your session has expired. Please log in again to continue.';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Network error: Please check your connection and try again.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }

      // Try to get more details from the response if available
      if (response) {
        try {
          const errorResponse = await response.text();
          if (errorResponse) {

            const errorData = JSON.parse(errorResponse);
            if (errorData.error) {
              errorMessage += `\nDetails: ${errorData.error}`;
            }
          }
        } catch (parseError) {
          // Ignore parsing errors
        }
      }

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Financial Assessment</h1>
              <p>Processing your responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Financial Assessment</h1>
              <p>Loading assessment questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel financial-panel"
        >
          <div className="assessment-header">
            <h1>Financial Assessment</h1>
            <p className="assessment-tool-info">
              Evaluating your financial well-being
            </p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            <h2 className="question-title">
              {currentQuestionData.question}
            </h2>

            <p className="question-instruction">
              Please rate on a scale from 1 to 10.
              <br />
              <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
                (1 = lowest well-being/highest distress; 10 = highest well-being)
              </span>
            </p>

            <div className="answer-options scale-options">
              {answerOptions.map((option) => (
                <label key={option.value} className="scale-option">
                  <input
                    type="radio"
                    name={`question-${currentQuestion}`}
                    value={option.value}
                    checked={currentResponse?.answer === option.value}
                    onChange={(e) => saveCurrentResponse(parseInt(e.target.value))}
                  />
                  <span className="scale-value">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
            >
              Back
            </button>

            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={!currentResponse}
            >
              {currentQuestion === totalQuestions ? 'Complete Assessment' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialAssessmentFormPage;

